import {noop, range} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import Immutable from 'immutable';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import BulkAddRowsToRegistrationTableFlow from 'bulk-import/BulkAddRowsToRegistrationTableFlow';
import DataConnector from 'connect/DataConnector';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import {forwardSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import registerSamples from 'notebook/registration-tables/registerSamples';
import {augmentRegisterOperation} from 'notebook/registration-tables/RegistrationTableColumnDefinitions';
import {
  RegistrationOperation,
  RegistrationOperationType,
  RegistrationTable as RegistrationTableData,
  RegistrationTableBaseProps,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {getSampleDetailsFromRegistrationTable} from 'notebook/registration-tables/RegistrationTableOps';
import RegistrationTableSettingsModal, {
  RegistrationTableSettingsModalValues,
} from 'notebook/registration-tables/RegistrationTableSettingsModal';
import {
  areRequiredSchemasArchived,
  getColumnDefinitionsFromStructuredTableColumns,
  getInsertableColumnInfos,
  getRegistrationTableRows,
  getRowWithDefaults,
  getTableRows,
  isRequiredBatchSchemaDeactivated,
  performColumnOpAndSetLabelingStrategy,
  RegistrationException,
} from 'notebook/registration-tables/RegistrationTableUtil';
import withRegistrationTableUtils, {
  RegistrationTableUtilitiesProps,
} from 'notebook/registration-tables/withRegistrationTableUtils';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import ManageSourcesModal from 'notebook/structured-tables/components/modals/ManageSourcesModal';
import PrintBarcodesButton from 'notebook/structured-tables/components/PrintBarcodesButton';
import RefreshStructuredTableButton from 'notebook/structured-tables/components/RefreshStructuredTableButton';
import TableHeaderPipeDivider from 'notebook/structured-tables/components/TableHeaderPipeDivider';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import prepareImportedRows, {
  ImportedStructuredTableRow,
} from 'notebook/structured-tables/spreadsheet-import/prepareImportedRows';
import {
  LegacyStructuredTableRow,
  SchemaFieldColumnUpdate,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {CollapsedTableReduxActions} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {MultiSelectionBox, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import excess from 'notebook/tables/excess';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import LookupField from 'notebook/tables/excess/functions/lookupField';
import {TokenUpdater} from 'notebook/tables/excess/ops/TokenUpdater';
import {getCombinedErrorString} from 'notebook/tables/excess/Util';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {LookupType} from 'notebook/tables/lookup-field/LookupFieldDefinitionConstants';
import {handleUpdateLookupFieldValues} from 'notebook/tables/lookup-field/LookupFieldUtil';
import {
  handleRefreshTableData,
  mergeColumnUpdatesIntoColumnConstraints,
} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import {fetchColumnUpdatesForTable} from 'notebook/tables/refresh-table-data/RefreshTableData';
import {
  Cell,
  Cells,
  RowData,
  Table,
  TableData,
  TableHeaderControlsArgs,
  TableOpDiffMap,
  UPDATE_LOOKUP_FIELD_MESSAGE,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  getLockedCellWarningMessage,
  getLockedTableWarningMessage,
  LockedCellWarningType,
  LockedTableWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import isBatchesEnabled from 'registry/IsBatchesEnabled';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import CreateRequestFromSamplesButton from 'requests/creation/CreateRequestFromSamplesButton';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import preserveShallowEquality from 'util/preserveShallowEquality';
import {initiateTextDownload} from 'util/TextToBlobUtil';
import {INPUT_SAMPLES_SOURCE_UUID, StageRun} from 'workflows/WorkflowConstants';

interface ConnectedProps {
  collapsedState: boolean | undefined;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class RegistrationTableConnector extends DataConnector<RegistrationTableProps, ConnectedProps> {
  override stores = [SchemaFieldSelectorStore];

  enhanceProps: () => ConnectedProps;

  constructor(props: RegistrationTableProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  uncachedEnhanceProps = (): ConnectedProps => {
    const schemaFields = [
      ...(this.props.tagSchema == null ? [] : this.props.tagSchema.fields),
      ...(this.props.batchSchema == null ? [] : this.props.batchSchema.fields),
    ];
    return {
      collapsedState:
        this.reduxState().ui.collapsedTables[
          createCollapseId(this.props.editorProps.fileId, this.props.tableName)
        ],
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(schemaFields)
      ),
    };
  };
}

interface RegistrationTableProps extends RegistrationTableBaseProps {
  stageRuns: Array<StageRun>;
  registrationRecordListId: string | null;
}

interface State {
  checkedRows: Immutable.Set<number> | null;
  isManageSourcesModalOpen: boolean;
}

/**
 * This is a legacy registration table, not a StructuredTable.
 *
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export class RegistrationTable extends React.Component<
  RegistrationTableProps & ConnectedProps & RegistrationTableUtilitiesProps,
  State
> {
  override state: State = {
    // Check all editable rows by default
    checkedRows: BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES
      ? Immutable.Set.of<number>(...this._getInitialCheckedRowIndices())
      : null,
    isManageSourcesModalOpen: false,
  };

  static selectorsLink = {
    // selectors are not enabled, but TableEditable still requires this
    value: [],
    selectorUsageCounts: {},
  };

  _getInitialCheckedRowIndices(): Array<number> {
    const rows = this.props.rows;
    return _.range(rows.length).filter((i) => !rows[i].isFinalized);
  }

  _getRegistrationTableData({
    newColumns,
    newRows,
    labelingStrategy,
  }: {
    newColumns?: Array<StructuredTableColumn>;
    newRows?: Array<LegacyStructuredTableRow>;
    labelingStrategy?: RegistryLabelingStrategy;
  }): RegistrationTableData {
    return {
      ...this.props.utils.getRegistrationTableData({newColumns, newRows, labelingStrategy}),
      registrationRecordListId: this.props.registrationRecordListId,
      type: NoteLinkedObjectType.REGISTRATION_TABLE,
    };
  }

  _mergeRowsAndColumnsIntoTable({
    index,
    newColumns,
    newRows,
    labelingStrategy,
    tableOp,
  }: {
    index: number;
    newColumns: Array<StructuredTableColumn>;
    newRows: Array<LegacyStructuredTableRow>;
    labelingStrategy?: RegistryLabelingStrategy;
    tableOp?: TableOpDiffMap;
  }): void {
    this.props.utils.mergeRowsAndColumnsIntoTable(
      index,
      this._getRegistrationTableData({
        newColumns,
        newRows,
        labelingStrategy,
      }),
      this.props.stageRuns,
      tableOp
    );
  }

  _handleSubmitSettingsModal = (
    registrationTableSettingsModalValues: RegistrationTableSettingsModalValues
  ): void => {
    const {labelingStrategy} = registrationTableSettingsModalValues;
    this.props.editorProps.storeState();
    if (labelingStrategy != null) {
      performColumnOpAndSetLabelingStrategy({
        table: this.props.table,
        performTableOpOnTable: ({op, options}) => {
          this.props.editorProps.performTableOp({tableName: this.props.table.name, op, options});
        },
        labelingStrategy,
      });
    }
  };

  _handleChangeManageSources = (sourceTables: Array<string>, newSources: Array<string>): void => {
    const isWatchingInputTable = sourceTables.concat(newSources).includes(INPUT_SAMPLES_SOURCE_UUID);

    this.props.updateHandler(this.props.index, {isWatchingInputTable}, undefined, {
      [this.props.table.name]: {type: 'eval-table'},
    });

    if (isWatchingInputTable !== this.props.isWatchingInputTable) {
      analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.TOGGLED_WATCHING_ON_REGISTRATION_TABLE, {
        isWatchingInputTable,
      });
    }

    this._handleCloseManageSourcesModal();
  };

  _handleUpdate = (
    index: number,
    {table}: {table: TableData},
    selectors: undefined,
    tableOp?: TableOpDiffMap
  ): void => {
    const newColumns = table.columnConstraints.map((constraint, i) => ({
      ...constraint!.structuredTableColumn!,
      width: table.widths[i],
    }));
    // handle new rows added via pull-down
    const oldRowCells = getTableRows(this.props.rows);
    const newRowCells = table.table.slice();
    const newRowData = table.rowData!.slice();
    // We are assuming that registered rows are always at the top of editable rows
    const newRowsIndices = _.range(oldRowCells.length, newRowCells.length);
    for (let i = oldRowCells.length; i < newRowCells.length; i++) {
      newRowCells[i] = getRowWithDefaults(newRowCells[i], this._getBlankRow(i), this._getColumnDefinitions());
      newRowData.push({isFinalized: false});
    }
    this._mergeRowsAndColumnsIntoTable({
      index,
      newColumns,
      newRows: getRegistrationTableRows(newRowCells, newRowData),
      tableOp,
    });

    if (BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES) {
      const checkedRows = this.state.checkedRows!.union(newRowsIndices);
      this.setState({checkedRows});
    }
  };

  _handleCheck = (index: number, state: boolean): void => {
    assert(BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES, 'Checkboxes expected to be enabled.');
    let checkedRows = this.state.checkedRows!;
    if (state) {
      checkedRows = checkedRows.add(index);
    } else {
      checkedRows = checkedRows.delete(index);
    }

    this.setState({checkedRows});
  };

  _handleCheckAll = (shouldCheckAll: boolean): void => {
    assert(BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES, 'Checkboxes expected to be enabled.');
    if (shouldCheckAll) {
      const numRows = this._getNumRegisteredRows() + this._getEditableRows().length;
      this.setState({
        checkedRows: Immutable.Set.of<number>(..._.range(numRows)),
      });
    } else {
      this.setState({
        checkedRows: Immutable.Set.of<number>(),
      });
    }
  };

  _getNumRegisteredRows(): number {
    return this.props.rows.filter((row) => row.isFinalized).length;
  }

  _getColumnDefinitions(): Array<ColumnDefinition> {
    return getColumnDefinitionsFromStructuredTableColumns(this.props.structuredTableColumns, {
      tagSchema: this.props.tagSchema,
      batchSchema: this.props.batchSchema,
      stageRuns: this.props.stageRuns,
    });
  }

  _createTable(): Table {
    const rowLabels = BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES
      ? this.props.table.table.map((row, i) => (
          <input
            type="checkbox"
            className={css(styles.checkbox)}
            checked={this.state.checkedRows ? this.state.checkedRows.has(i) : false}
            onChange={(e) => {
              this._handleCheck(i, e.target.checked);
            }}
          />
        ))
      : null;

    const rowHeader = BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES ? (
      <td
        key="indeterminate-checkbox"
        className="mediocre-tableEditable-axisCell mediocre-tableEditable-axisCell--header"
      >
        <IndeterminateCheckbox
          disabled={false}
          items={_.range(this.props.table.table.length)}
          selectedMap={(i) => this.state.checkedRows!.has(i)}
          onChange={(e) => {
            this._handleCheckAll(e.target.checked);
          }}
        />
      </td>
    ) : null;

    return {
      ...this.props.table,
      metadata: {labelingStrategy: this.props.labelingStrategy},
      rowLabels,
      _rowHeader: rowHeader,
    };
  }

  _getUncheckedEditableRows(): Array<Array<Cell>> {
    assert(BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES, 'Checkboxes expected to be enabled.');
    return getTableRows(
      this.props.rows.filter((row, i) => this.state.checkedRows!.has(i) === false && !row.isFinalized)
    );
  }

  _getEditableRows(): Array<Array<Cell>> {
    return getTableRows(this.props.rows.filter((row) => !row.isFinalized));
  }

  /**
   * May throw RegistrationException if there is a problem to report to the user.
   */
  _getEvaluatedRowsForRegistration(): Array<Array<TypedValue>> {
    const evaluatedRows = [];
    for (const [i, evaluatedRow] of this.props.computedTable.entries()) {
      if (BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES) {
        if (!this.state.checkedRows!.has(i)) {
          continue;
        }
      } else if (this._isRowFinalized(i)) {
        continue;
      }
      for (const cell of evaluatedRow) {
        if (cell.error) {
          throw new RegistrationException(getCombinedErrorString(cell.error));
        }
      }
      evaluatedRows.push(evaluatedRow.map(({value}) => value));
    }
    return evaluatedRows;
  }

  _getRegisteredRows(): Array<Array<Cell>> {
    return getTableRows(this.props.rows.filter((row) => row.isFinalized));
  }

  /**
   * Return an array of operations to be submitted to the registration API endpoint.
   *
   * May throw RegistrationException if there is a problem to report to the user.
   */
  _getOperations(): Array<RegistrationOperation> {
    const columnDefinitions = this._getColumnDefinitions();
    const operations = [];
    const evaluatedRows = this._getEvaluatedRowsForRegistration();
    for (const evaluatedRow of evaluatedRows) {
      let operation: RegistrationOperation = {
        operationType:
          this.props.tableType === RegistrationTableType.FILE
            ? RegistrationOperationType.REGISTER_NEW_FILE
            : RegistrationOperationType.REGISTER_NEW_FILE_WITH_BATCH,
        stageRunId: null,
        tagsDict: {},
        batchTagsDict: {},
      };
      for (const [i, columnDefinition] of columnDefinitions.entries()) {
        operation = augmentRegisterOperation({
          operation,
          typedValue: evaluatedRow[i],
          columnDefinition,
          tagSchema: this.props.tagSchema,
          batchSchema: this.props.batchSchema,
          tableType: this.props.tableType,
        });
      }
      operations.push(operation);
    }
    return operations;
  }

  _handleRegister = async (): Promise<void> => {
    let operations;
    try {
      operations = this._getOperations();
    } catch (err) {
      if (err instanceof RegistrationException) {
        notifications.error(err.message);
        return;
      } else {
        throw err;
      }
    }

    const originalCollapsedState = this.props.collapsedState;
    CollapsedTableReduxActions.set(this.props.cid, true);

    const tableRowsNumber = this._getNumRegisteredRows() + this._getEditableRows().length;

    try {
      await registerSamples({
        entryId: this.props.editorProps.fileId,
        registrationTableProvenanceUuid: this.props.provenanceUuid,
        operations,
        rowIndicesToRegister: this._getRowIndicesToRegister(tableRowsNumber),
      });
      if (BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES) {
        this.setState({checkedRows: Immutable.Set.of()});
      }
    } catch (err) {
      if (net.isServerRequestError(err)) {
        notifications.stickyNotifyForFriendlyError(err, 'registration-table-fail');
        return;
      }
      throw err;
    } finally {
      assert(originalCollapsedState != null, 'Collapsed state is expected to be set.');
      CollapsedTableReduxActions.set(this.props.cid, originalCollapsedState);
    }
  };

  _getBlankRow(index: number): Array<Cell> {
    const stageRunName = this.props.stageRuns.length === 1 ? this.props.stageRuns[0].name : null;
    return getBlankRow(index, this.props.structuredTableColumns, stageRunName);
  }

  _onImportRows = (importedRows: Array<ImportedStructuredTableRow>): void => {
    const columnDefinitions = this._getColumnDefinitions();
    const newRowCells = prepareImportedRows(
      importedRows,
      columnDefinitions.map((columnDefinition) => ({
        columnDefinition,
        spreadsheetColumnType: getSpreadsheetTypeForColumn(ModelTypeConstants.TAG_SCHEMA, columnDefinition),
      }))
    );
    const oldRows = this.props.rows;
    const startIndex = oldRows.length;
    const endIndex = startIndex + newRowCells.length;
    const newRows = newRowCells.map((cells) => ({cells}));

    const startingRowIndex = oldRows.length;
    const newRowsWithDefaultValues = newRows.map((row, newRowIndex) => {
      const structuredTableRow = this._getBlankRow(startingRowIndex + newRowIndex);
      Object.entries(row.cells).forEach(([colIndex, cell]) => {
        if (!TableUtil.isCellEmpty(cell)) {
          structuredTableRow[Number(colIndex)] = cell;
        }
      });
      return structuredTableRow;
    });

    const rows = [...oldRows, ...newRowsWithDefaultValues.map((cells) => ({cells, isFinalized: false}))];

    this._mergeRowsAndColumnsIntoTable({
      index: this.props.index,
      newColumns: this.props.structuredTableColumns,
      newRows: rows,
      tableOp: {[this.props.table.name]: {type: 'insert-rows', rowIndexes: range(startIndex, endIndex - 1)}},
    });
    if (BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES) {
      this.setState({
        checkedRows: this.state.checkedRows!.union(_.range(startIndex, endIndex)),
      });
    }
  };

  _handleImportFromSpreadsheet = (): void => {
    BulkAddRowsToRegistrationTableFlow.open({
      tagSchemaId: this.props.tagSchema.id,
      batchSchemaId:
        this.props.tableType === RegistrationTableType.BATCH ? this.props.tagSchema.batchSchemaId : null,
      columnDefinitions: this._getColumnDefinitions(),
      onImportRows: this._onImportRows,
    });
  };

  // This is no longer used at all.
  _handleRemoveCheckedRows = (): void => {
    assert(BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES, 'Checkboxes expected to be enabled.');
    this.props.editorProps.storeState();

    const uncheckedEditableRows = this._getUncheckedEditableRows();
    const numRegisteredRows = this._getNumRegisteredRows();
    if (uncheckedEditableRows.length === 0 && numRegisteredRows === 0) {
      this._mergeRowsAndColumnsIntoTable({
        index: this.props.index,
        newColumns: this.props.structuredTableColumns,
        newRows: getRegistrationTableRows([this._getBlankRow(0)], [{isFinalized: false}]),
        tableOp: {[this.props.table.name]: {type: 'eval-table'}},
      });
      this.setState({checkedRows: Immutable.Set.of(0)});
    } else {
      this.props.editorProps.performTableOp({
        tableName: this.props.table.name,
        op: (table, documentName) => {
          const oldRows = this.props.rows;
          const tokenUpdaters: Array<TokenUpdater> = [];
          let updatedTable = table;
          for (let i = oldRows.length - 1; i >= 0; i--) {
            if (this.state.checkedRows!.has(i) !== false) {
              const opOutput = excess.ops.deleteRows(updatedTable, documentName, i, i);
              updatedTable = opOutput.table;
              tokenUpdaters.push(...opOutput.tokenUpdaters);
            }
          }
          return {table: updatedTable, tokenUpdaters, box: null, dim: TableDimension.ROW};
        },
        options: {ignoreLockedDimensions: true},
      });
      this.setState({checkedRows: Immutable.Set.of()});
    }
  };

  _renderAddButton = ({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null => {
    if (isCollapsed || this._tableIsLocked()) {
      return null;
    }
    return (
      <Dropdown
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FIT_CONTENT}>
            <Option
              icon="upload"
              onSelect={() => {
                this._handleImportFromSpreadsheet();
              }}
            >
              Import from spreadsheet
            </Option>
          </Menu>
        }
      >
        <RichObjectTable.AddButton />
      </Dropdown>
    );
  };

  _handleOpenSettingsModal = (): void => {
    legacyRunModal(RegistrationTableSettingsModal, {
      labelingStrategy: this.props.labelingStrategy,
      locked: !this._canLabelingStrategyBeChanged(),
      onRequestSubmit: this._handleSubmitSettingsModal,
      tagSchemaId: this.props.tagSchema.id,
    });
  };

  _handleOpenManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: true});
  };

  _handleCloseManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: false});
  };

  /**
   * Only allow changing the labeling strategy if the editor is unlocked and we
   * don't have any registered rows.
   */
  _canLabelingStrategyBeChanged = (): boolean =>
    !(this._tableIsLocked() || this._getRegisteredRows().length > 0);

  _renderSettingsModalButton(): JSX.Element {
    return (
      <Tooltip title="Settings">
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-left"
            icon="settings"
            disabled={this.props.editor.isProtectedAgainstEdit()}
            onClick={this._handleOpenSettingsModal}
            size={ButtonSize.MINI}
          />
        </span>
      </Tooltip>
    );
  }

  _renderManageSourcesModalButton(): JSX.Element | null {
    if (APIUtil.isNotebookEntryOrTemplate(this.props.editorProps.fileId)) {
      return null;
    }
    const sources = this.props.isWatchingInputTable ? [INPUT_SAMPLES_SOURCE_UUID] : [];
    return (
      <span>
        <ModalAnchor open={this.state.isManageSourcesModalOpen}>
          {this.state.isManageSourcesModalOpen && (
            <ManageSourcesModal
              initialSourceTableUuids={sources}
              onRequestClose={this._handleCloseManageSourcesModal}
              onRequestSubmit={this._handleChangeManageSources}
            />
          )}
        </ModalAnchor>
        <Tooltip title="Manage sources">
          <span>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              className="u-mini-spacing-left"
              disabled={this._tableIsLocked()}
              icon="sources"
              onClick={this._handleOpenManageSourcesModal}
              size={ButtonSize.MINI}
              text={sources.length.toString()}
            />
          </span>
        </Tooltip>
      </span>
    );
  }

  _handleDownloadAsCSV = async (): Promise<void> => {
    const {table: cells, name, columnLabels} = this._createTable();
    const headerRow = columnLabels.map((columnLabel) => columnLabel || 'unknown');
    initiateTextDownload(
      await makeTableCSV({
        table: cells,
        computedTable: this.props.computedTable,
        headerRow,
      }),
      `${name}.csv`
    );
    analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.DOWNLOADED_REGISTRATION_TABLE_AS_CSV);
  };

  _renderExportToCSVButton(): JSX.Element {
    return <SimpleDownloadAsCSVButton onDownloadAsCSV={this._handleDownloadAsCSV} />;
  }

  _renderCreateRequestButton(): JSX.Element {
    const {tagSchema, batchSchema} = this.props;
    const samples = getSampleDetailsFromRegistrationTable(this._getRegistrationTableData({})).map(
      (sample) => ({
        ...sample,
        schema: this.props.tableType === RegistrationTableType.FILE ? tagSchema : batchSchema,
      })
    );

    return (
      <CreateRequestFromSamplesButton
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        samples={samples}
        size={ButtonSize.MINI}
      />
    );
  }

  _renderPrintButton(multiSelectionBox: MultiSelectionBox | null): JSX.Element | null {
    return (
      <>
        <PrintBarcodesButton
          table={this.props.table}
          multiSelectionBox={multiSelectionBox}
          columns={this.props.structuredTableColumns}
          computedTable={this.props.computedTable}
        />
        <TableHeaderPipeDivider />
      </>
    );
  }

  _renderHeaderControls = ({isCollapsed, multiSelectionBox}: TableHeaderControlsArgs): JSX.Element => {
    if (isCollapsed) {
      return this._renderExportToCSVButton();
    }
    const onlyUnregisteredRowsChecked = this._areOnlyUnregisteredRowsChecked();

    let registerTooltipTitle = 'Register';
    if (isRequiredBatchSchemaDeactivated(this.props.tableType, this.props.batchSchema)) {
      registerTooltipTitle = 'Batch schema must be active to register';
    } else if (this._areRequiredSchemasArchived()) {
      registerTooltipTitle = 'Schema must be un-archived to register';
    } else if (!onlyUnregisteredRowsChecked) {
      registerTooltipTitle = 'Select only unregistered rows to register';
    } else if (this._hasValidationErrorsInRowsToRegister()) {
      registerTooltipTitle = 'Fix validation errors to register';
    }

    const table = this._createTable();

    const doRefresh = async (): Promise<void> => {
      await handleRefreshTableData({
        table,
        computedTable: this.props.computedTable,
        onUpdateTable: ({
          updatedCells,
          columnUpdates,
        }: {
          updatedCells: Cells;
          columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
        }) => {
          const columnConstraints = mergeColumnUpdatesIntoColumnConstraints(
            table,
            columnUpdates,
            this.props.editorProps.getIdFromCid
          );
          const newTable = {...table, table: updatedCells, columnConstraints};
          this._handleUpdate(this.props.index, {table: newTable}, undefined, {
            [this.props.table.name]: {type: 'eval-table'},
          });
        },
        fetchColumnUpdates: () =>
          fetchColumnUpdatesForTable({
            entryOrTemplateId: this.props.editorProps.fileId,
            provenanceUuid: this.props.provenanceUuid,
          }),
      });
    };

    return (
      <span className="u-vertical-center-items">
        {this._renderPrintButton(multiSelectionBox)}
        {BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES && (
          <Tooltip title={onlyUnregisteredRowsChecked ? 'Remove' : 'Select only unregistered rows to remove'}>
            <span>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className="u-mini-spacing-left"
                disabled={this._tableIsLocked() || !onlyUnregisteredRowsChecked}
                icon="delete"
                onClick={this._handleRemoveCheckedRows}
                size={ButtonSize.MINI}
              />
            </span>
          </Tooltip>
        )}
        {BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES && <TableHeaderPipeDivider />}
        <RefreshStructuredTableButton
          entryOrTemplateId={this.props.editorProps.fileId}
          doRefresh={doRefresh}
          structuredTableKind="OLD Registration table"
          disabled={this._tableIsLocked()}
        />
        {BenchlingConfig.ENABLE_REQUESTS && this._renderCreateRequestButton()}
        {this._renderExportToCSVButton()}
        {this._renderSettingsModalButton()}
        {this._renderManageSourcesModalButton()}
        <Tooltip title={registerTooltipTitle}>
          <span>
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              className="u-mini-spacing-left"
              disabled={
                this._tableIsLocked() ||
                !onlyUnregisteredRowsChecked ||
                this._hasValidationErrorsInRowsToRegister()
              }
              icon="register"
              onClick={this._handleRegister}
              size={ButtonSize.MINI}
              text="Register"
            />
          </span>
        </Tooltip>
      </span>
    );
  };

  _isRowFinalized(i: number): boolean {
    return this.props.rows[i] && this.props.rows[i].isFinalized;
  }

  _areOnlyUnregisteredRowsChecked(): boolean {
    if (!BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES) {
      return Boolean(this._getEditableRows().length);
    } else {
      if (!this._getEditableRows().length || !this.state.checkedRows!.count()) {
        return false;
      }
      for (const i of this.state.checkedRows!) {
        if (this._isRowFinalized(i)) {
          return false;
        }
      }
      return true;
    }
  }

  _getRowIndicesToRegister(tableRowsNumber: number): Array<number> {
    return BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES
      ? _.range(tableRowsNumber).filter((i) => this.state.checkedRows && this.state.checkedRows.has(i))
      : _.range(tableRowsNumber).filter((i) => !this._isRowFinalized(i));
  }

  _hasValidationErrorsInRowsToRegister(): boolean {
    const rowsIndicesToRegister = this._getRowIndicesToRegister(this.props.computedTable.length);
    for (const i of rowsIndicesToRegister) {
      const evaluatedRow = this.props.computedTable[i];
      for (const evaluatedCell of evaluatedRow) {
        if (evaluatedCell.error) {
          return true;
        }
      }
    }
    return false;
  }

  _areRequiredSchemasArchived(): boolean {
    return areRequiredSchemasArchived(this.props.tableType, this.props.tagSchema, this.props.batchSchema);
  }

  _getLockedTableWarningType(): LockedTableWarningType {
    if (isRequiredBatchSchemaDeactivated(this.props.tableType, this.props.batchSchema)) {
      return LockedTableWarningType.DEACTIVATED_BATCH_SCHEMA;
    }
    if (this._areRequiredSchemasArchived()) {
      return LockedTableWarningType.ARCHIVED_SCHEMA;
    }
    return LockedTableWarningType.GENERIC;
  }

  _getEditWhileCellLockedWarning = (multiSelectionBox: MultiSelectionBox | null): string => {
    let type;
    if (multiSelectionBox && multiSelectionBox.minI < this._getNumRegisteredRows()) {
      type = LockedCellWarningType.ALREADY_REGISTERED;
    } else {
      type = LockedCellWarningType.GENERIC;
    }
    return getLockedCellWarningMessage(type);
  };

  _tableIsLocked(): boolean {
    return (
      this.props.editorProps.locked ||
      !isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE}) ||
      (this.props.tableType === RegistrationTableType.BATCH && !isBatchesEnabled()) ||
      this._areRequiredSchemasArchived()
    );
  }

  override render(): JSX.Element {
    const {tagSchema, batchSchema, selectorsById} = this.props;
    const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(
      this.props.editor,
      this.props.editorProps
    );
    const table = this._createTable();
    return (
      <TableEditable
        {...allEditorProps}
        locked={this._tableIsLocked()}
        isProtected={this.props.editor.isProtectedAgainstEdit()}
        additionalFunctions={
          APIUtil.isNotebookEntryOrTemplate(this.props.editorProps.fileId)
            ? []
            : [
                {
                  name: LookupField.name,
                  asyncOnClick: ({
                    rowData,
                    evaluatedRow,
                    onUpdateSelectedCell,
                  }: {
                    rowData: RowData;
                    evaluatedRow: Array<EvaluatedCell>;
                    onUpdateSelectedCell: (cellData: Partial<Cell>) => void;
                  }) =>
                    LookupField.onCreateFormula({
                      entryType: APIUtil.apiIdToType(this.props.editorProps.fileId),
                      stageConfigurationId: null,
                      supportedLookupTypes: [LookupType.STAGE_RUN_INPUT, LookupType.EXPERIMENTAL_CONDITION],
                      rowData,
                      evaluatedRow,
                      columnDefinitions: this._getColumnDefinitions(),
                      onUpdateSelectedCell,
                    }),
                },
              ]
        }
        additionalSettings={
          APIUtil.isNotebookEntryOrTemplate(this.props.editorProps.fileId)
            ? []
            : [{name: UPDATE_LOOKUP_FIELD_MESSAGE, applySettingToSelection: handleUpdateLookupFieldValues}]
        }
        canAddRemoveColumns={!this._getNumRegisteredRows()}
        cellColors={this.props.cellColors}
        cid={this.props.cid}
        computedTable={this.props.computedTable}
        collapseId={createCollapseId(this.props.editorProps.fileId, table.name)}
        disableFormulas={false}
        disableLegacyHeader={true}
        disableMaximizedTables={
          allEditorProps.disableMaximizedTables || BenchlingConfig.ENABLE_REGISTRATION_TABLE_CHECKBOXES
        }
        getEditWhileCellLockedWarning={this._getEditWhileCellLockedWarning}
        editWhileTableLockedWarning={getLockedTableWarningMessage(this._getLockedTableWarningType())}
        headerControls={(args) => this._renderHeaderControls(args)}
        leftHeaderControls={(args) => this._renderAddButton(args)}
        icon={this.props.icon}
        indentation={this.props.indentation}
        index={this.props.index}
        insertableColumnInfos={getInsertableColumnInfos(
          tagSchema,
          batchSchema,
          this.props.structuredTableColumns,
          selectorsById
        )}
        isCollapsible={true}
        isLeftAlignColumnHeader={true}
        isMaximized={this.props.isMaximized}
        disableMergingCells={true}
        metadataForRestore={this.props.metadataForRestore}
        mouseDownHandler={this.props.mouseDownHandler}
        mouseOverHandler={this.props.mouseOverHandler}
        multiEditing={this.props.multiEditing}
        openManageSelectorsModal={noop}
        selectorsEnabled={false}
        selectorsLink={RegistrationTable.selectorsLink}
        showExcessLabels={this.props.showExcessLabels}
        showFormulaBox={true}
        tableLabel={this.props.tableTypeLabel}
        table={table}
        tableNamespace={this.props.tableNamespace}
        updateHandler={this._handleUpdate}
        {...forwardSearchResultProps(this.props)}
      />
    );
  }
}

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

/**
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default RegistrationTableConnector.connect(withRegistrationTableUtils(RegistrationTable));
